import type { ReactNode } from "react"
import { IconCaretRightFill18 as CaretIcon } from "nucleo-ui-fill-18"

import { cn } from "@productlane/lib/src/cn"

// Tailwind needs the group-hover class as a literal, so each caller's group
// name gets its own entry rather than being interpolated.
const CHEVRON_REVEAL = {
	"docs-section": "group-hover/docs-section:opacity-100",
	"inbox-section": "group-hover/inbox-section:opacity-100",
} as const

export type NavSectionHoverGroup = keyof typeof CHEVRON_REVEAL

// Section label for a nav sidebar (the docs nav, the inbox nav). Pass
// `isOpen`/`onToggle` to make it collapsible; without them it renders as a
// plain label. An expanded section reveals its chevron on hover of
// `group/<hoverGroup>`, so the caller owns that class on whatever wrapper spans
// the header and its rows.
export const NavSectionHeader = ({
	label,
	isOpen,
	onToggle,
	action,
	className,
	hoverGroup = "docs-section",
}: {
	label: string
	isOpen?: boolean
	onToggle?: () => void
	action?: ReactNode
	className?: string
	hoverGroup?: NavSectionHoverGroup
}) => {
	const collapsible = isOpen !== undefined && onToggle !== undefined

	return (
		<div
			className={cn(
				"group/section flex items-center gap-1 pr-1 text-tertiary",
				className,
			)}
		>
			{collapsible ? (
				<button
					type="button"
					onClick={onToggle}
					aria-expanded={isOpen}
					className="group/toggle flex min-w-0 flex-1 cursor-default select-none items-center gap-1 self-stretch rounded-md pl-2 text-left text-xs font-medium hover:text-secondary"
				>
					<span className="truncate">{label}</span>
					{/* Collapsed keeps the chevron visible as the cue that content is
					    hidden; expanded is the resting state, so it waits for section hover. */}
					<CaretIcon
						className={cn(
							"size-2 flex-none transform-gpu transition-transform",
							isOpen
								? cn(
										"rotate-90 opacity-0 group-focus-visible/toggle:opacity-100",
										CHEVRON_REVEAL[hoverGroup],
									)
								: "rotate-0",
						)}
					/>
				</button>
			) : (
				<span className="min-w-0 flex-1 select-none truncate pl-2 text-xs font-medium">
					{label}
				</span>
			)}
			{action}
		</div>
	)
}

// Every collapsible nav section opens and closes with this one height and fade,
// so Workspace, Views and Assistant move alike.
export const NavSectionBody = ({
	isOpen,
	inert = !isOpen,
	className,
	innerClassName,
	children,
}: {
	isOpen: boolean
	inert?: boolean
	className?: string
	innerClassName?: string
	children: ReactNode
}) => (
	<div
		inert={inert}
		className={cn(
			"ease-[cubic-bezier(0.23,1,0.32,1)] grid transition-[grid-template-rows,opacity] duration-200",
			isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-40",
			className,
		)}
	>
		<div className={cn("min-h-0 overflow-hidden", innerClassName)}>
			{children}
		</div>
	</div>
)
